import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { api } from '../api/client';
import { API_PROFILE_URL, isTokenValid } from '../utils';
import { getSessionUserId, useOnSessionChange } from '../utils/session';

/**
 * The signed-in user's own record (the /api/user-info private projection —
 * includes avatar), shared by the header, the profile page, and anywhere
 * else that renders "me". Follows the PreferencesContext pattern: load()
 * no-ops until there is a valid token, route changes re-arm it, and
 * refresh() re-fetches after a profile/avatar edit so every consumer
 * updates at once.
 */
const CurrentUserContext = createContext(null);

export function CurrentUserProvider({ children }) {
	const [user, setUser] = useState(null);
	// Has the question "who is signed in?" been answered? With no token it is
	// answered at once (signed out); with one it waits for the /user-info
	// fetch to settle, either way. Until then the shell shows a neutral
	// placeholder, never the signed-out state (platform-standards §14).
	const [resolved, setResolved] = useState(() => !isTokenValid());
	const loadedRef = useRef(false);   // a fetch SUCCEEDED for this login
	const inFlightRef = useRef(false); // dedupe concurrent triggers only
	const location = useLocation();

	// Another account signing in on this tab without passing /logout kept the
	// last one's record (the latch only re-armed on a signed-out route) — and
	// with it the header's name and avatar and the personal collection id.
	const session = useOnSessionChange(() => {
		loadedRef.current = false;
		inFlightRef.current = false; // a request for the last account must not block this one's
		setUser(null);
		setResolved(!isTokenValid());
	});

	const load = useCallback(async ({ force = false } = {}) => {
		if (!isTokenValid()) {
			loadedRef.current = false; // logged out — re-arm for next login
			setUser(null);
			setResolved(true);
			return;
		}
		if ((loadedRef.current || inFlightRef.current) && !force) return;
		inFlightRef.current = true;
		const startedFor = getSessionUserId();
		try {
			const { data, status } = await api.get(API_PROFILE_URL);
			if (getSessionUserId() !== startedFor) return; // answered for the last account
			if (status === 200) {
				setUser(data);
				// Only success latches — a transient failure leaves the next
				// route change free to retry instead of a session-long blank.
				loadedRef.current = true;
			}
		} catch {
			// 401 handled globally by the api client; transient errors leave
			// the previous value in place and the latch unarmed.
		} finally {
			inFlightRef.current = false;
			if (getSessionUserId() === startedFor) setResolved(true);
		}
	}, []);

	useEffect(() => {
		load();
	}, [load, location.pathname, session]);

	const refresh = useCallback(() => load({ force: true }), [load]);

	// The account's personal collection (GET /api/user-info answers with
	// it — keepr-api docs/SCHEMA.md § The personal collection). Null while
	// loading, and on an API a deploy behind that does not know it yet.
	const personalCollectionId = user?.personalCollectionId || null;

	// True while a signed-in person's record is still on its way: a token
	// exists, no answer yet. False when signed out or once the fetch settled.
	const loading = !user && !resolved;

	const value = useMemo(() => ({ user, refresh, personalCollectionId, loading }), [user, refresh, personalCollectionId, loading]);

	return (
		<CurrentUserContext.Provider value={value}>
			{children}
		</CurrentUserContext.Provider>
	);
}

/** Null-tolerant: returns {user: null, loading: false, refresh: no-op} without a provider. */
export function useCurrentUser() {
	return useContext(CurrentUserContext) || { user: null, refresh: () => {}, personalCollectionId: null, loading: false };
}
